import React, { useState, useRef, useCallback, useEffect } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import * as Router from "react-router-dom";
import { useSelector } from "react-redux";
import { Agent } from "model/agent";
import { getUser } from "redux/selector";
import { enhancedBioEnabled, mlsSetupRefresh } from "shared/env";
import { RootState, useAppThunkDispatch } from "redux/store";
import { fetchAgentMlsIds } from "redux/slice/agent-mls-ids";
import { fetchClientBoards } from "redux/slice/client-boards";
import { profileBackLink } from "routes/profile-navigation";
import { styles } from "./styles";
import { Summary } from "./summary";
import { ProfileTab } from "./tab-profile";
import { AccessTab } from "./tab-access";
import { WebsiteTab } from "./tab-website";
import { EmailHeaderTab } from "./tab-email-header";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { TabHandle } from "./tab-handle";

interface Props extends OwnProps, WithStyles<typeof styles>, Router.RouteComponentProps {}

interface OwnProps {
	agent: Agent;
	/**
	 * Rendered as the logged-in member's own Profile page rather than the team's member-detail view
	 * (MAX-4139). Passed in rather than derived from `user.agentId === agent.id` so the member-detail
	 * route's behaviour cannot change just because an admin opens their own row from the team table.
	 */
	selfView?: boolean;
}

const TAB_LABELS = ["Profile", "Access", "Website", "Email Header"];
// Self-view renames the last tab: it carries the listing-alert toggle as well as the header, so it
// is "Emails" rather than "Email Header". Single-agent accounts keep the tab and lose only the
// header section inside it — that account shape has no brokerage branding for a header to sit under.
const SELF_TAB_LABELS = ["Profile", "Access", "Website", "Emails"];

const ProfileRefreshComponent: React.FC<Props> = ({ agent, selfView = false, classes = {}, history, location }) => {
	const user = useSelector(getUser);
	const isEnhancedBioEnabled = useSelector(
		(state: RootState) => enhancedBioEnabled && state.brokerSettings.isEnhancedBioEnabled
	);
	const [tab, setTab] = useState(0);
	const [saving, setSaving] = useState(false);
	const [saveSuccess, setSaveSuccess] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	// Re-render the footer when a tab reports its dirty state changed.
	const [, setDirtyTick] = useState(0);
	const tabRef = useRef<TabHandle>(null);
	// Stable identity so tab effects that depend on it don't re-fire every render (avoids a loop).
	const onDirtyChange = useCallback(() => setDirtyTick((t) => t + 1), []);
	const dispatch = useAppThunkDispatch();
	const agentId = agent?.id;
	// With the MLS Setup redesign on, the Profile tab's MLS IDs and the summary's list both read the agent MLS IDs.
	// Loaded here, fresh for each profile, because the summary shows on every tab. The Edit/Set modal refetches
	// after a write. Only on accounts that can read agent MLS IDs; the rest keep the agent-board editor.
	const usesAgentMlsIds = mlsSetupRefresh && user.permissions.officeAccountType === true;
	useEffect(() => {
		if (usesAgentMlsIds) {
			dispatch(fetchAgentMlsIds());
			dispatch(fetchClientBoards());
		}
	}, [dispatch, agentId, usesAgentMlsIds]);

	if (!agent) {
		return null;
	}

	const canViewTabs = user.admin || user.agentId === agent.id;
	// "Single agent" is an account shape, not a headcount: permissions.broker is false for accounts
	// with no team context at all. Those lose the Email Header tab.
	const isSingleAgent = selfView && !user.permissions.broker;
	const tabLabels = selfView ? SELF_TAB_LABELS : TAB_LABELS;

	const handleSave = async () => {
		if (!tabRef.current) {
			return;
		}
		setSaveSuccess(false);
		setSaveError(null);
		if (tabRef.current.validate && !tabRef.current.validate()) {
			return;
		}
		setSaving(true);
		try {
			await tabRef.current.save();
			setSaveSuccess(true);
		} catch(e) {
			setSaveError("Your changes could not be saved. Please try again.");
		} finally {
			// finally, so a throwing save can't strand the button on "Saving...".
			setSaving(false);
			onDirtyChange();
		}
	};

	const handleCancel = () => {
		tabRef.current?.cancel();
		setSaveSuccess(false);
		setSaveError(null);
		// Deliberately no onDirtyChange() here. cancel() sets state on the tab, and the tab's own
		// dirty effect fires once React has flushed it. Bumping the tick synchronously instead made
		// this component re-render first and read isDirty() off the pre-reset state, which left the
		// save bar enabled and made Cancel look like it had done nothing (MAX-4139).
	};

	const dirty = tabRef.current ? tabRef.current.isDirty() : false;
	const backLink = profileBackLink(location.state);

	return (
		<div className={classes.root}>
			<UnsavedChangesPrompt isDirty={() => (tabRef.current ? tabRef.current.isDirty() : false)} history={history} />
			<div className={classes.content}>
			{!selfView && (
				<Mui.Link className={classes.backLink} onClick={() => history.push(backLink.path)}>
					<Icons.ArrowLeft size={16} />
					{backLink.label}
				</Mui.Link>
			)}

			<div className={classes.layout}>
				<div className={classes.leftPanel}>
					<Summary agent={agent} isSingleAgent={isSingleAgent} isSelfView={selfView} classes={classes} />
				</div>

				{canViewTabs && (
					<div className={classes.rightPanel}>
						<div className={classes.tabBar}>
							{tabLabels.map((label, index) => (
								<button
									key={label}
									type="button"
									className={`${classes.tab} ${index === tab ? classes.tabActive : ""}`}
									onClick={() => {
										setTab(index);
										setSaveSuccess(false);
										setSaveError(null);
									}}
								>
									<span className={classes.tabLabel}>{label}</span>
									<span className={`${classes.tabUnderline} ${index === tab ? classes.tabUnderlineActive : ""}`} />
								</button>
							))}
						</div>

						{/* key forces a fresh tab instance (+ ref) on tab change */}
						{tab === 0 && <ProfileTab key="profile" ref={tabRef} agent={agent} selfView={selfView} isSingleAgent={isSingleAgent} classes={classes} onDirtyChange={onDirtyChange} />}
						{tab === 1 && <AccessTab key="access" ref={tabRef} agent={agent} selfView={selfView} isSingleAgent={isSingleAgent} classes={classes} onDirtyChange={onDirtyChange} />}
						{tab === 2 && <WebsiteTab key="website" ref={tabRef} agent={agent} selfView={selfView} classes={classes} isEnhancedBioEnabled={isEnhancedBioEnabled} onDirtyChange={onDirtyChange} />}
						{tab === 3 && <EmailHeaderTab key="email" ref={tabRef} agent={agent} selfView={selfView} isSingleAgent={isSingleAgent} classes={classes} onDirtyChange={onDirtyChange} />}
					</div>
				)}
			</div>
			</div>

			{canViewTabs && (
				<SettingsStickyFooter
					fullBleed={selfView}
					dirty={dirty}
					saving={saving}
					saveSuccess={saveSuccess}
					saveError={saveError}
					onCancel={handleCancel}
					onSave={handleSave}
					onSaveFeedbackClose={() => {
						setSaveSuccess(false);
						setSaveError(null);
					}}
				/>
			)}
		</div>
	);
};

export const ProfileRefresh = withStyles(Router.withRouter(ProfileRefreshComponent), styles);
